// frontend/src/components/dashboard/AIAssistantWidget.tsx

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Send, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAuthStore } from '../../store/authStore';
import { usePlanUsage } from '../../hooks/usePlanUsage';
import { geminiService, GeminiApiError } from '../../services/gemini.service';
import { ActiveCourseSummary } from '../../types/dashboard.types';

interface AIAssistantWidgetProps {
  activeCourses?: ActiveCourseSummary[];
}

type Step = 'ask_time' | 'generating' | 'done' | 'quota_exceeded';

const QUICK_OPTIONS = ['15 dakika', '30 dakika', '1 saat', '2 saat'];

export function AIAssistantWidget({ activeCourses = [] }: AIAssistantWidgetProps) {
  const profile = useAuthStore((s) => s.profile);
  const { usage, isAtLimit } = usePlanUsage();

  const [step, setStep] = useState<Step>('ask_time');
  const [input, setInput] = useState('');
  const [plan, setPlan] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const planRef = useRef<HTMLDivElement>(null);

  const aiQuotaExceeded = isAtLimit('ai_message');
  const lessonQuotaExceeded = isAtLimit('lesson');

  // Plan üretilince scroll
  useEffect(() => {
    if (step === 'done' && planRef.current) {
      planRef.current.scrollTop = 0;
    }
  }, [step]);

  async function generatePlan(timeInput: string) {
    if (!timeInput.trim() || isLoading) return;
    setInput('');
    setIsLoading(true);
    setStep('generating');
    setError(null);

    try {
      // Kullanıcı bağlamını oluştur
      const courseList = activeCourses.length > 0
        ? activeCourses.map(c => `• ${c.title} (%${c.progressPercent} tamamlandı)`).join('\n')
        : 'Henüz aktif ders yok.';

      const aiLimit = usage?.aiMessagesLimit === null ? 'sınırsız' : `${usage?.aiMessagesUsed ?? 0}/${usage?.aiMessagesLimit ?? '?'}`;
      const lessonLimit = usage?.lessonsLimit === null ? 'sınırsız' : `${usage?.lessonsUsed ?? 0}/${usage?.lessonsLimit ?? '?'}`;

      const prompt = `Sen MinikCoder akademisinin AI Yol Haritası (Roadmap) asistanısın. Türkçe yazacaksın.

Öğrenci bilgileri:
- Ad: ${profile?.full_name?.split(' ')[0] ?? 'Öğrenci'}
- Yaş: ${profile?.age ?? 'Belirtilmemiş'}
- XP: ${profile?.xp ?? 0}
- Aktif dersler:
${courseList}
- AI mesaj kullanımı: ${aiLimit} (${aiQuotaExceeded ? 'KOTA DOLDU' : 'kullanılabilir'})
- Ders kullanımı: ${lessonLimit} (${lessonQuotaExceeded ? 'KOTA DOLDU' : 'kullanılabilir'})
- Paketi: ${usage?.planName ?? 'Bilinmiyor'}

Öğrenci bugün çalışmak için: "${timeInput}" ayırdı.

Buna göre, öğrencinin aktif derslerini temel alan, sırasıyla ne yapması gerektiğini ("Önce X konusunu çalış, ardından Y alıştırmasını tamamla, sonra Z projesine göz at" şeklinde) açıklayan adım adım ve uygulanabilir bir "Öğrenim Yol Haritası (Roadmap)" oluştur.

Kurallar:
1. Tamamen aktif olan dersleri temel al (başka kurs/ders uydurma).
2. Adımları ve sırayı net şekilde belirt. Numaralı liste (1., 2., 3.) kullan.
3. Eğer ders veya AI mesaj kotası dolmuşsa, o aktiviteyi planlamadan önce kullanıcıya dostça bir kota uyarısı yap.
4. Emojilerle süslü ama gereksiz detaylardan uzak tut (maksimum 150 kelime).
5. Yanıtı tamamen Markdown formatında ver (Başlıklar için ###, kalın yazılar için ** kullan).
6. Sonda motive edici 1 kısa cümle yaz.`;

      const response = await geminiService.sendChatMessage(prompt, [], {
        model: 'gemini-3.5-flash-lite',
        studentContext: {
          age: profile?.age ?? undefined,
          currentCourse: activeCourses[0]?.title,
        },
      });

      setPlan(response.reply);
      setStep('done');
    } catch (err) {
      if (err instanceof GeminiApiError && err.code === 'QUOTA_EXCEEDED') {
        setStep('quota_exceeded');
      } else {
        setError(err instanceof Error ? err.message : 'Plan oluşturulamadı.');
        setStep('ask_time');
      }
    } finally {
      setIsLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    generatePlan(input);
  }

  function handleReset() {
    setStep('ask_time');
    setPlan('');
    setError(null);
  }

  return (
    <div className="nd-card nd-ai-card">
      <div className="nd-ai-header-row">
        <h2 className="nd-card-title">
          <span className="nd-card-title-icon">🤖</span>
          Roadmap AI
        </h2>
        {step === 'done' && (
          <button className="nd-ai-reset-btn" onClick={handleReset} title="Yeni yol haritası oluştur">
            🔄 Yeni Plan
          </button>
        )}
      </div>

      {/* Robot + bubble */}
      <div className="nd-ai-robot-wrap">
        <div className={`nd-ai-robot ${isLoading ? 'nd-ai-robot--thinking' : ''}`}>🤖</div>
        <div className="nd-ai-bubble">
          {step === 'ask_time' && (
            <p>
              {aiQuotaExceeded
                ? '⚠️ AI mesaj kotanız doldu. Paketi yükseltirseniz yol haritası oluşturabilirim!'
                : `Merhaba ${profile?.full_name?.split(' ')[0] ?? ''}! 👋 Bugün ne kadar çalışmak istersin?`}
            </p>
          )}
          {step === 'generating' && (
            <p className="nd-ai-thinking">
              <Loader2 size={14} className="nd-spin" />
              Yol haritan hazırlanıyor...
            </p>
          )}
          {step === 'done' && (
            <p>✅ Yol haritan hazır! Başarılar diliyorum 🚀</p>
          )}
          {step === 'quota_exceeded' && (
            <p>⚠️ AI kota limitine ulaşıldı. Yol haritasını oluşturmak için paketini yükselt.</p>
          )}
        </div>
      </div>

      {/* Plan çıktısı */}
      {step === 'done' && plan && (
        <div className="nd-ai-plan-output" ref={planRef}>
          <div className="nd-ai-markdown-content">
            <ReactMarkdown>{plan}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Hata */}
      {error && (
        <p className="nd-ai-error">{error}</p>
      )}

      {/* Hızlı seçenekler */}
      {step === 'ask_time' && !aiQuotaExceeded && (
        <div className="nd-ai-quick-opts">
          {QUICK_OPTIONS.map(opt => (
            <button
              key={opt}
              className="nd-ai-quick-btn"
              onClick={() => generatePlan(opt)}
              disabled={isLoading}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {/* Input alanı */}
      {(step === 'ask_time' || step === 'generating') && !aiQuotaExceeded && (
        <form onSubmit={handleSubmit} className="nd-ai-input-row">
          <input
            type="text"
            className="nd-ai-input"
            placeholder="Örn: 45 dakika, 1.5 saat..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
            autoComplete="off"
          />
          <button
            type="submit"
            className="nd-ai-send-btn"
            aria-label="Plan Oluştur"
            disabled={isLoading || !input.trim()}
          >
            {isLoading ? <Loader2 size={15} className="nd-spin" /> : <Send size={15} />}
          </button>
        </form>
      )}

      {/* Kota doldu — upgrade link */}
      {(step === 'quota_exceeded' || aiQuotaExceeded) && (
        <Link to="/pricing" className="nd-ai-upgrade-btn">
          🚀 Paketi Yükselt
        </Link>
      )}
    </div>
  );
}
